/**
 * RecipientCombobox - chip-row input that suggests email addresses
 * from four signal sources (company, thread, history, directory).
 * Used by the RecipientHeader's To / Cc / Bcc rows.
 *
 * Behavior:
 *   - On focus, opens the dropdown with the empty-query result
 *     (company directory). FreshDesk does the same: clicking the
 *     field shows everyone at the requester's company.
 *   - As the agent types, the query is debounced (180ms) and the
 *     dropdown refreshes.
 *   - Arrow Up / Down move the highlight; Enter commits the
 *     highlighted suggestion. If nothing is highlighted, Enter
 *     commits the literal typed text (same as Gmail).
 *   - Comma / semicolon also commit (literal text).
 *   - Escape closes the dropdown without committing.
 *   - Mouse click on a row commits.
 *
 * The component is unaware of which row it's on (To / Cc / Bcc); the
 * parent passes onCommit which routes to the right state slot.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { useRecipientSuggestions } from "@/hooks/useRecipientSuggestions";
import type { RecipientSuggestion } from "@/types/api";

export interface RecipientComboboxProps {
  ticketNumber: number;
  ariaLabel: string;
  placeholder?: string;
  // Called whenever a chip should be added. The string is already
  // trimmed and lowercased; the parent can dedupe its own list.
  onCommit: (email: string) => void;
  isPending?: boolean;
  // Emails already in the chip column. Suggestions matching these
  // are hidden from the dropdown so an agent can't double-add.
  excludeEmails?: readonly string[];
}

function isEmailish(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

const SOURCE_LABEL: Record<RecipientSuggestion["source"], string> = {
  company: "Same company",
  thread: "On this ticket",
  history: "Previous tickets",
  directory: "Directory",
};

export function RecipientCombobox({
  ticketNumber,
  ariaLabel,
  placeholder,
  onCommit,
  isPending,
  excludeEmails = [],
}: RecipientComboboxProps): JSX.Element {
  const inputId = useId();
  const listboxId = `${inputId}-listbox`;
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimerRef = useRef<number | null>(null);

  const [value, setValue] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);

  // 180ms debounce on the suggestion query. Short enough to feel live,
  // long enough that we don't fire one request per keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value.trim()), 180);
    return () => window.clearTimeout(t);
  }, [value]);

  const query = useRecipientSuggestions(ticketNumber, debounced, {
    enabled: open && Number.isFinite(ticketNumber),
  });

  // Exclude addresses already in the chip column (case-insensitive).
  const excludeSet = useMemo(
    () => new Set(excludeEmails.map((e) => e.toLowerCase())),
    [excludeEmails],
  );
  const items = useMemo(
    () =>
      (query.data ?? []).filter(
        (s) => !excludeSet.has(s.email.toLowerCase()),
      ),
    [query.data, excludeSet],
  );

  // Reset highlight when results change.
  useEffect(() => {
    setHighlightIndex(0);
  }, [items.length]);

  function commitLiteral() {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (!isEmailish(trimmed)) {
      setValidationError("Enter a valid email address.");
      return;
    }
    onCommit(trimmed.toLowerCase());
    setValue("");
    setDebounced("");
    setValidationError(null);
  }

  function commitSuggestion(s: RecipientSuggestion) {
    onCommit(s.email.toLowerCase());
    setValue("");
    setDebounced("");
    setValidationError(null);
    setOpen(true);
    // Keep focus so the agent can add another without re-clicking.
    inputRef.current?.focus();
  }

  function handleEnter(e: React.KeyboardEvent<HTMLInputElement>) {
    if (open && items.length > 0 && highlightIndex < items.length) {
      e.preventDefault();
      commitSuggestion(items[highlightIndex]!);
      return;
    }
    e.preventDefault();
    commitLiteral();
  }

  return (
    <div className="relative min-w-[10rem] flex-1">
      <input
        ref={inputRef}
        id={inputId}
        type="email"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && items.length > 0
            ? `${listboxId}-${highlightIndex}`
            : undefined
        }
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          if (validationError) setValidationError(null);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Delay close so click on a dropdown row registers before
          // the input loses focus. The mousedown handler on rows
          // cancels this timer.
          blurTimerRef.current = window.setTimeout(() => {
            setOpen(false);
            // Blur-commit: agent typed an address and clicked away
            // (mouse left the field entirely). Mirrors gmail.
            if (value.trim()) {
              commitLiteral();
            }
          }, 120);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleEnter(e);
            return;
          }
          if (e.key === "," || e.key === ";") {
            e.preventDefault();
            commitLiteral();
            return;
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setHighlightIndex((i) =>
              items.length === 0 ? 0 : Math.min(i + 1, items.length - 1),
            );
            return;
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightIndex((i) => Math.max(i - 1, 0));
            return;
          }
          if (e.key === "Escape") {
            setOpen(false);
            return;
          }
        }}
        disabled={isPending}
        className="w-full border-0 bg-transparent px-1 py-0.5 text-sm text-neutral-800 placeholder:text-neutral-400 focus:outline-none disabled:opacity-50"
        data-testid="recipient-combobox-input"
      />
      {validationError && (
        <div role="alert" className="mt-1 text-xs text-red-600">
          {validationError}
        </div>
      )}
      {open && (
        <ul
          id={listboxId}
          role="listbox"
          data-testid="recipient-combobox-listbox"
          className="absolute left-0 right-0 top-full z-30 mt-1 max-h-72 overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-lg"
          // Stop the input's blur from firing when the user clicks
          // a row; commitSuggestion then re-focuses cleanly.
          onMouseDown={(e) => {
            if (blurTimerRef.current !== null) {
              window.clearTimeout(blurTimerRef.current);
              blurTimerRef.current = null;
            }
            e.preventDefault();
          }}
        >
          {query.isLoading && items.length === 0 && (
            <li className="px-3 py-2 text-xs text-neutral-400">
              Loading suggestions...
            </li>
          )}
          {!query.isLoading && items.length === 0 && (
            <li className="px-3 py-2 text-xs text-neutral-400">
              {debounced
                ? "No matching contacts. Press Enter to add as a new address."
                : "No company contacts found."}
            </li>
          )}
          {items.map((s, i) => {
            const isActive = i === highlightIndex;
            return (
              <li
                key={`${s.email}-${s.source}`}
                id={`${listboxId}-${i}`}
                role="option"
                aria-selected={isActive}
                data-testid="recipient-combobox-option"
                onMouseEnter={() => setHighlightIndex(i)}
                onClick={() => commitSuggestion(s)}
                className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-sm ${
                  isActive ? "bg-primary-50 text-primary-900" : "text-neutral-800"
                }`}
              >
                <div className="min-w-0">
                  <div className="truncate">
                    {s.name ? (
                      <>
                        <span className="font-medium">{s.name}</span>{" "}
                        <span className="text-neutral-500">
                          &lt;{s.email}&gt;
                        </span>
                      </>
                    ) : (
                      <span>{s.email}</span>
                    )}
                  </div>
                </div>
                <span className="shrink-0 text-[10px] uppercase tracking-wide text-neutral-400">
                  {SOURCE_LABEL[s.source]}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default RecipientCombobox;
